import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Heart, Circle, Activity, ClipboardList, Car, Timer, Brain, ListChecks, Eye, HeartPulse, Pill } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface SummaryData {
  totalUsers: number;
  totalCircles: number;
  roomAssessments: number;
  drivingAssessments: number;
  tugTests: number;
  thriveScores: number;
  focusOlderAdults: number;
  adlAssessments: number;
  iadlAssessments: number;
  visionAssessments: number;
  wellnessVitals: number;
  medications: number;
}

interface AppSummaryCardsProps {
  data?: SummaryData;
  isLoading: boolean;
}

const summaryCards = [
  { key: 'totalUsers', label: 'Total Users', icon: Users, color: 'text-blue-500' },
  { key: 'focusOlderAdults', label: 'Older Adults', icon: Heart, color: 'text-rose-500' },
  { key: 'totalCircles', label: 'ThriveCircles', icon: Circle, color: 'text-emerald-500' },
  { key: 'roomAssessments', label: 'Room Scans', icon: ClipboardList, color: 'text-amber-500' },
  { key: 'drivingAssessments', label: 'Driving Tests', icon: Car, color: 'text-purple-500' },
  { key: 'tugTests', label: 'TUG Tests', icon: Timer, color: 'text-cyan-500' },
  { key: 'thriveScores', label: 'ThriveScores', icon: Activity, color: 'text-green-500' },
  { key: 'adlAssessments', label: 'ADL Assessments', icon: Brain, color: 'text-indigo-500' },
  { key: 'iadlAssessments', label: 'IADL Assessments', icon: ListChecks, color: 'text-pink-500' },
  { key: 'visionAssessments', label: 'Vision Tests', icon: Eye, color: 'text-sky-500' },
  { key: 'wellnessVitals', label: 'Vital Logs', icon: HeartPulse, color: 'text-red-500' },
  { key: 'medications', label: 'Medications', icon: Pill, color: 'text-orange-500' },
];

export const AppSummaryCards: React.FC<AppSummaryCardsProps> = ({ data, isLoading }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
      {summaryCards.map((card) => {
        const Icon = card.icon;
        const value = data?.[card.key as keyof SummaryData] ?? 0;
        
        return (
          <Card key={card.key} className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-muted ${card.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  {isLoading ? (
                    <Skeleton className="h-7 w-12" />
                  ) : (
                    <p className="text-2xl font-bold text-foreground">{value.toLocaleString()}</p>
                  )}
                  <p className="text-xs text-muted-foreground">{card.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
